import React, { useState, useEffect } from "react";
import { Typography, Stack, Button, Divider, Box, TextField, MenuItem, CircularProgress } from "@mui/material";
import FocusedFlowPage from "components/FocusedFlowPage";
import { useNavigate, useLocation } from "react-router-dom";
import { postAliquotCheckoutComplete } from "utils/api";
import { useSnackbar } from "components/snackbar/SnackbarProvider";
import { US_STATES } from "constants/patientOptions";
import {
  chicagoDateTimeLocalToIso,
  isoToChicagoDateTimeLocal,
} from "utils/appDateTime";
import { BIOBANK_SUB_ROUTES } from "../../../constants/routes";

const NAME_REGEX = /^[A-Za-z\s'-]+$/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const ZIP_REGEX = /^\d{5}$/;
const PHONE_REGEX = /^\d{10}$/;

const stripToDigits = (value, maxLength) => value.replace(/\D/g, "").slice(0, maxLength);

const AliquotStorageLocationDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || BIOBANK_SUB_ROUTES.ALIQUOT_MANAGEMENT_CHECK_OUT;
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    recipientName: "",
    recipientInstitution: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    phoneNumber: "",
    email: "",
    shipOutDate: isoToChicagoDateTimeLocal(),
    shippedByName: "",
    shippedByPhoneNumber: "",
    shippedByEmail: "",
  });

  const { openSnackbar } = useSnackbar();

  useEffect(() => {
    if (location.state && location.state.samples) {
      setSamples(location.state.samples);
    }
  }, [location.state]);

  const handleBack = () => {
    navigate(returnTo);
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleDigitsChange = (field, maxLength) => (e) => {
    setFormData({ ...formData, [field]: stripToDigits(e.target.value, maxLength) });
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.recipientName.trim()) {
      errors.recipientName = "Recipient Name is required";
    } else if (!NAME_REGEX.test(formData.recipientName)) {
      errors.recipientName = "Recipient Name can only contain letters, spaces, hyphens, and apostrophes";
    }

    if (!formData.recipientInstitution.trim()) {
      errors.recipientInstitution = "Recipient Institution is required";
    }

    if (!formData.streetAddress.trim()) {
      errors.streetAddress = "Street Address is required";
    }

    if (!formData.city.trim()) {
      errors.city = "City is required";
    } else if (!NAME_REGEX.test(formData.city)) {
      errors.city = "City can only contain letters, spaces, hyphens, and apostrophes";
    }

    if (!formData.state) {
      errors.state = "State is required";
    }

    if (!formData.zipCode.trim()) {
      errors.zipCode = "Zip Code is required";
    } else if (!ZIP_REGEX.test(formData.zipCode)) {
      errors.zipCode = "Zip Code must be exactly 5 digits";
    }

    if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = "Phone Number is required";
    } else if (!PHONE_REGEX.test(formData.phoneNumber)) {
      errors.phoneNumber = "Phone Number must be exactly 10 digits";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!EMAIL_REGEX.test(formData.email)) {
      errors.email = "Invalid email format";
    }

    if (!formData.shipOutDate) {
      errors.shipOutDate = "Ship Out Date is required";
    }

    if (!formData.shippedByName.trim()) {
      errors.shippedByName = "Sender Name is required";
    } else if (!NAME_REGEX.test(formData.shippedByName)) {
      errors.shippedByName = "Sender Name can only contain letters, spaces, hyphens, and apostrophes";
    }

    if (!formData.shippedByPhoneNumber.trim()) {
      errors.shippedByPhoneNumber = "Sender Phone is required";
    } else if (!PHONE_REGEX.test(formData.shippedByPhoneNumber)) {
      errors.shippedByPhoneNumber = "Sender Phone must be exactly 10 digits";
    }

    if (!formData.shippedByEmail.trim()) {
      errors.shippedByEmail = "Sender Email is required";
    } else if (!EMAIL_REGEX.test(formData.shippedByEmail)) {
      errors.shippedByEmail = "Invalid email format";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleContinue = async () => {
    if (!validateForm()) {
      openSnackbar("Please correct the highlighted fields.", "error");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        aliquotIds: samples.map((s) => s.aliquotId),
        shipment: {
          recipientName: formData.recipientName.trim(),
          recipientInstitution: formData.recipientInstitution.trim(),
          streetAddress: formData.streetAddress.trim(),
          city: formData.city.trim(),
          state: formData.state.trim(),
          zipCode: formData.zipCode.trim(),
          phoneNumber: formData.phoneNumber.trim(),
          email: formData.email.trim(),
          shipOutDate: chicagoDateTimeLocalToIso(formData.shipOutDate),
          shippedByName: formData.shippedByName.trim(),
          shippedByPhoneNumber: formData.shippedByPhoneNumber.trim(),
          shippedByEmail: formData.shippedByEmail.trim(),
        },
      };

      const response = await postAliquotCheckoutComplete(payload);

      if (response.failedAliquots?.length) {
        const failures = response.failedAliquots.map(item => `${item.aliquotId}: ${item.reason}`).join(", ");
        openSnackbar(`${response.completedCount} checked out; ${response.failedAliquots.length} failed. ${failures}`, "warning");
      } else {
        openSnackbar("Aliquots checked out successfully", "success");
        navigate(returnTo);
      }
    } catch (error) {
      console.error("Error checking out aliquots:", error);
      let errorMsg = "Error checking out aliquots";
      if (error.data?.status === 'unconfirmed' && error.data?.recordType === 'shipment') {
        errorMsg = `Shipment write unconfirmed. ID: ${error.data.recordId}. Reconcile before retrying.`;
      } else if (error.data?.failedAliquots?.length) {
        const failedStr = error.data.failedAliquots.map(fa => `${fa.aliquotId}: ${fa.reason}`).join(", ");
        errorMsg = `Checkout has no confirmed aliquots. Shipment ${error.data.shipmentId || 'not created'}. ${failedStr}`;
      }
      openSnackbar(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <FocusedFlowPage
      title="Aliquot Shipment Details"
      onClose={() => navigate(returnTo)}
      maxWidth={860}
      actions={[
        <Button
          key="back"
          variant="outlined"
          onClick={handleBack}
          disabled={loading}
          sx={{
            borderRadius: 0,
            borderColor: "#B28F38",
            color: "#B28F38",
            fontWeight: 600,
            px: 4,
            py: 1,
            "&:hover": { borderColor: "#8c6e25", backgroundColor: "transparent" },
          }}
        >
          Go Back
        </Button>,
        <Button
          key="submit"
          variant="contained"
          onClick={handleContinue}
          disabled={loading}
          sx={{
            borderRadius: 0,
            backgroundColor: "#500000",
            fontWeight: 600,
            px: 4,
            py: 1,
            "&:hover": { backgroundColor: "#3a0000" },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Complete Aliquot Check-Out"}
        </Button>,
      ]}
    >
      <Typography variant="h6" sx={{ textAlign: "center", mb: 0.5 }}>
        Are you sure you want to check out all of the following Aliquots?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mb: 2 }}>
        Scroll to view all selected aliquots.
      </Typography>

      <Box sx={{ mb: 4, maxHeight: 200, overflowY: 'auto', mx: 'auto', maxWidth: 800, textAlign: 'center' }}>
        <Stack spacing={2}>
          {samples.map((item, index) => (
            <Typography key={index} variant="body1" color="#000000">
              <strong>{item.aliquotId}</strong>, Freezer: {item.freezer}, Rack:{" "}
              {item.rack}, Box: {item.box}, Position: {item.position}
            </Typography>
          ))}
        </Stack>
      </Box>

      <Typography variant="h4" color="primary" sx={{ textAlign: 'center', mt: 8 }}>Check-Out Details</Typography>
      <Divider sx={{ marginY: 2, mb: 4 }} />

      <Box sx={{ maxWidth: "700px", margin: "0 auto" }}>
        <Stack spacing={3}>
          <Typography variant="h5" color="primary" sx={{ mt: 6, mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
            Recipient Information
          </Typography>

          <Box sx={{ display: "flex", gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Field
                label="Recipient Name"
                required
                value={formData.recipientName}
                onChange={handleChange("recipientName")}
                error={validationErrors.recipientName}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Field
                label="Recipient Institution"
                required
                value={formData.recipientInstitution}
                onChange={handleChange("recipientInstitution")}
                error={validationErrors.recipientInstitution}
              />
            </Box>
          </Box>

          <Field
            label="Street Address"
            required
            multiline
            rows={3}
            value={formData.streetAddress}
            onChange={handleChange("streetAddress")}
            error={validationErrors.streetAddress}
          />

          <Box sx={{ display: "flex", gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Field
                label="City"
                required
                value={formData.city}
                onChange={handleChange("city")}
                error={validationErrors.city}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Field
                select
                label="State"
                required
                value={formData.state}
                onChange={handleChange("state")}
                error={validationErrors.state}
              >
                {US_STATES.map((stateOption) => (
                  <MenuItem key={stateOption.value} value={stateOption.value}>
                    {stateOption.label}
                  </MenuItem>
                ))}
              </Field>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Field
                label="Zip Code"
                required
                value={formData.zipCode}
                onChange={handleDigitsChange("zipCode", 5)}
                error={validationErrors.zipCode}
                placeholder="12345"
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Field
                label="Phone Number"
                required
                value={formData.phoneNumber}
                onChange={handleDigitsChange("phoneNumber", 10)}
                error={validationErrors.phoneNumber}
                placeholder="1234567890"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Field
                label="Email"
                required
                value={formData.email}
                onChange={handleChange("email")}
                error={validationErrors.email}
              />
            </Box>
          </Box>

          <Typography variant="h5" color="primary" sx={{ mt: 6, mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
            Sender Information
          </Typography>

          <Box sx={{ display: "flex", gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Field
                label="Ship Out Date"
                required
                type="datetime-local"
                value={formData.shipOutDate}
                onChange={handleChange("shipOutDate")}
                error={validationErrors.shipOutDate}
              />
            </Box>
            <Box sx={{ flex: 1 }} />
          </Box>

          <Box sx={{ display: "flex", gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Field
                label="Sender Name"
                required
                value={formData.shippedByName}
                onChange={handleChange("shippedByName")}
                error={validationErrors.shippedByName}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Field
                label="Sender Phone"
                required
                value={formData.shippedByPhoneNumber}
                onChange={handleDigitsChange("shippedByPhoneNumber", 10)}
                error={validationErrors.shippedByPhoneNumber}
                placeholder="1234567890"
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Field
                label="Sender Email"
                required
                value={formData.shippedByEmail}
                onChange={handleChange("shippedByEmail")}
                error={validationErrors.shippedByEmail}
              />
            </Box>
            <Box sx={{ flex: 1 }} />
          </Box>
        </Stack>
      </Box>
    </FocusedFlowPage>
  );
};

const Field = ({
  label,
  required,
  value,
  onChange,
  error,
  type = "text",
  placeholder,
  multiline,
  rows,
  select,
  children
}) => (
  <Box>
    {label && (
      <Typography sx={{ fontWeight: 600, fontSize: "18px", color: "#3E3E3E", mb: "6px" }}>
        {label}
        {required && <span style={{ color: "red", marginLeft: "3px" }}>*</span>}
      </Typography>
    )}
    <TextField
      fullWidth
      type={type}
      value={value}
      onChange={onChange}
      error={!!error}
      helperText={error}
      placeholder={placeholder}
      multiline={multiline}
      rows={rows}
      select={select}
      InputLabelProps={{ shrink: true }}
      sx={{
        input: {
          padding: "10px",
          fontSize: "16px",
          backgroundColor: "white",
          borderRadius: "6px",
        },
        textarea: {
          padding: "10px",
          fontSize: "16px",
          backgroundColor: "white",
          borderRadius: "6px",
        },
        ".MuiSelect-select": {
          padding: "10px",
          fontSize: "16px",
          backgroundColor: "white",
          borderRadius: "6px",
        }
      }}
    >
      {children}
    </TextField>
  </Box>
);

export default AliquotStorageLocationDetails;
